import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";
import UppyUploader, { type UppyUploadItem, type UppyUploaderHandle } from "@/components/UppyUploader";
import { HFButton } from "@/components/ui-hf";
import { mediaThumb } from "@/lib/mediaThumb";
import ErrorBoundary from "@/components/ErrorBoundary";
import { UploadCloud, Trash2, RefreshCw, Video, X, RotateCcw, AlertCircle, CheckCircle2, Clock, Sparkles, ChevronDown, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;
const MULTIPART_THRESHOLD = 100 * 1024 * 1024;
const CHUNK_CONCURRENCY = 3;
const MAX_BATCH_SIZE = 10;
const ALLOWED_MIME = new Set(["image/jpeg", "image/png", "image/webp", "video/mp4"]);
const PENDING_STORAGE_KEY = "hf_multipart_pending";

/* ─── localStorage-Persistenz für unterbrochene Multipart-Uploads ───── */
type PendingUpload = {
    mediaId: string;
    uploadId: string;
    key: string;
    filename: string;
    size: number;
    contentType: string;
    chunkSize: number;
    completedParts: Array<{ PartNumber: number; ETag: string }>;
    startedAt: number;
};

function loadPending(): PendingUpload[] {
    try {
        const raw = localStorage.getItem(PENDING_STORAGE_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
    } catch {
        return [];
    }
}

function savePending(list: PendingUpload[]) {
    try {
        localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(list));
    } catch { /* quota / private mode → ignore */ }
}

function upsertPending(entry: PendingUpload) {
    const list = loadPending().filter((p) => p.uploadId !== entry.uploadId);
    list.push(entry);
    savePending(list);
}

function removePending(uploadId: string) {
    savePending(loadPending().filter((p) => p.uploadId !== uploadId));
}

type Media = {
    id: string; original_filename: string; stored_filename: string; mime: string;
    size?: number; size_bytes?: number; media_type: string; width?: number; height?: number;
    poster_filename?: string | null; captured_at?: string | null;
    title: string; description: string; alt: string; order: number;
    visible: boolean; download_allowed: boolean; project_id?: string | null;
    created_at: string;
    stored_url?: string | null; poster_url?: string | null;
    processed_at?: string | null; processing_error?: string | null;
    duration?: number | null; codec?: string | null;
    is_demo?: boolean;
    is_ai_generated?: boolean;
    source?: string | null;
    pair_id?: string | null;
    pair_role?: "before" | "after" | null;
    hls_ready?: boolean;
    hls_url?: string | null;
};

type UsageBlock = {
    mediaId: string;
    projects: { slug: string; title: string; usage: "cover" | "gallery" }[];
    sections?: { section_key: string; section_title: string; usage: "site_content" }[];
};

type UsageEntry = {
    project_id?: string;
    project_slug?: string;
    project_title?: string;
    type: "cover" | "gallery" | "pair_before" | "pair_after" | "site_content";
    pair_title?: string;
    section_key?: string;
    section_title?: string;
};

type ProjectLite = { id: string; slug: string; title: string };

type QueueStatus = "queued" | "uploading" | "done" | "error" | "cancelled";

type QueueItem = {
    id: string;
    file: File;
    status: QueueStatus;
    mode: "direct" | "multipart";
    uploaded: number;    // bytes
    speed: number;       // bytes/sec
    etaSec: number;
    startedAt?: number;  // ms
    error?: string;
    resume?: PendingUpload;  // wenn gesetzt, wird via existing uploadId fortgesetzt
};

type CancelHandle = {
    xhr?: XMLHttpRequest;
    mp?: { key: string; upload_id: string; media_id: string };
    cancelRequested: boolean;
};

const MEDIA_TYPES = [
    "photo", "photo_360", "video", "video_360",
    "fisheye", "tiny_planet", "before", "after",
];

const TYPE_LABEL: Record<string, string> = {
    photo: "FOTO", photo_360: "360° FOTO", video: "VIDEO", video_360: "360° VIDEO",
    fisheye: "FISHEYE", tiny_planet: "TINY PLANET", before: "VORHER", after: "NACHHER",
};

function fmtMB(bytes: number) {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
function fmtEta(sec: number) {
    if (!isFinite(sec) || sec <= 0) return "";
    if (sec < 60) return `${Math.ceil(sec)}s`;
    if (sec < 3600) {
        const m = Math.floor(sec / 60);
        const s = Math.ceil(sec % 60);
        return `${m}m ${s}s`;
    }
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${m}m`;
}
function uid(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/* ─── Direct upload (< 100 MB) via XHR mit Cancel-Support ─────────────── */
function uploadDirect(
    file: File,
    onProgress: (loaded: number, total: number, speed: number, etaSec: number) => void,
    onXhr: (xhr: XMLHttpRequest) => void,
): Promise<Media> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        onXhr(xhr);
        const fd = new FormData();
        fd.append("file", file, file.name);
        let lastLoaded = 0;
        let lastT = performance.now();
        let speed = 0;
        xhr.upload.addEventListener("progress", (e) => {
            if (!e.lengthComputable) return;
            const now = performance.now();
            const dt = (now - lastT) / 1000;
            if (dt > 0.15) {
                const inst = (e.loaded - lastLoaded) / dt;
                speed = speed === 0 ? inst : speed * 0.6 + inst * 0.4;
                lastLoaded = e.loaded;
                lastT = now;
            }
            const remaining = speed > 0 ? (e.total - e.loaded) / speed : 0;
            onProgress(e.loaded, e.total, speed, remaining);
        });
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try { resolve(JSON.parse(xhr.responseText)); }
                catch { reject(new Error("Server-Antwort nicht lesbar")); }
            } else {
                let msg = `HTTP ${xhr.status}`;
                try { msg = JSON.parse(xhr.responseText).detail || msg; } catch { /* noop */ }
                reject(new Error(msg));
            }
        };
        xhr.onerror = () => reject(new Error("Netzwerkfehler beim Upload"));
        xhr.onabort = () => reject(new Error("Abgebrochen"));
        xhr.withCredentials = true;
        xhr.open("POST", `${BACKEND_URL}/api/admin/media`);
        xhr.send(fd);
    });
}

/* ─── Multipart upload (≥ 100 MB) mit Cancel + Resume-Support ────────── */
async function uploadMultipart(
    file: File,
    onProgress: (loaded: number, total: number, speed: number, etaSec: number) => void,
    onCtx: (ctx: { key: string; upload_id: string; media_id: string }) => void,
    isCancelled: () => boolean,
    resume?: PendingUpload,
): Promise<Media> {
    let media_id: string;
    let key: string;
    let upload_id: string;
    let chunk_size: number;
    let completedParts: Array<{ PartNumber: number; ETag: string }>;

    if (resume) {
        // Server-Side aktive Parts holen (Source of Truth), mit localStorage mergen
        const statusRes = await api<{ active: boolean; parts: Array<{ PartNumber: number; ETag: string; Size: number }> }>(
            `/api/admin/media/multipart/status?upload_id=${encodeURIComponent(resume.uploadId)}&key=${encodeURIComponent(resume.key)}`,
        );
        if (!statusRes.ok || !statusRes.data || !statusRes.data.active) {
            throw new Error("Der unterbrochene Upload ist bei R2 nicht mehr aktiv (evtl. abgelaufen). Bitte neu starten.");
        }
        media_id = resume.mediaId;
        key = resume.key;
        upload_id = resume.uploadId;
        chunk_size = resume.chunkSize;
        completedParts = statusRes.data.parts.map((p) => ({ PartNumber: p.PartNumber, ETag: p.ETag }));
    } else {
        const initRes = await api<{ media_id: string; key: string; upload_id: string; chunk_size: number }>(
            "/api/admin/media/multipart/init",
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ filename: file.name, content_type: file.type, size: file.size }),
            },
            { retry: true, aggressiveRetry: true },
        );
        if (!initRes.ok || !initRes.data) throw new Error(initRes.error || "Init fehlgeschlagen");
        media_id = initRes.data.media_id;
        key = initRes.data.key;
        upload_id = initRes.data.upload_id;
        chunk_size = initRes.data.chunk_size;
        completedParts = [];
        // Sofort in localStorage sichern
        upsertPending({
            mediaId: media_id, uploadId: upload_id, key, filename: file.name,
            size: file.size, contentType: file.type, chunkSize: chunk_size,
            completedParts: [], startedAt: Date.now(),
        });
    }
    onCtx({ key, upload_id, media_id });

    const totalParts = Math.ceil(file.size / chunk_size);
    const parts: Array<{ PartNumber: number; ETag: string } | undefined> = new Array(totalParts);
    // Bereits vorhandene Parts eintragen
    const doneSet = new Set<number>();
    for (const p of completedParts) {
        parts[p.PartNumber - 1] = { PartNumber: p.PartNumber, ETag: p.ETag };
        doneSet.add(p.PartNumber);
    }
    let uploadedBytes = 0;
    for (const p of completedParts) {
        const start = (p.PartNumber - 1) * chunk_size;
        const end = Math.min(start + chunk_size, file.size);
        uploadedBytes += (end - start);
    }
    const started = performance.now();
    const startedUploadedBytes = uploadedBytes;
    let speed = 0;
    let lastReport = 0;

    const persistProgress = () => {
        upsertPending({
            mediaId: media_id, uploadId: upload_id, key, filename: file.name,
            size: file.size, contentType: file.type, chunkSize: chunk_size,
            completedParts: parts.filter(Boolean) as Array<{ PartNumber: number; ETag: string }>,
            startedAt: Date.now(),
        });
    };

    const reportProgress = () => {
        const elapsed = (performance.now() - started) / 1000;
        speed = elapsed > 0 ? (uploadedBytes - startedUploadedBytes) / elapsed : 0;
        const eta = speed > 0 ? (file.size - uploadedBytes) / speed : 0;
        onProgress(uploadedBytes, file.size, speed, eta);
        lastReport = performance.now();
    };

    const uploadPart = async (partNumber: number) => {
        if (isCancelled()) throw new Error("Abgebrochen");
        const start = (partNumber - 1) * chunk_size;
        const end = Math.min(start + chunk_size, file.size);
        const chunk = file.slice(start, end);

        const signRes = await api<{ url: string }>(
            "/api/admin/media/multipart/sign-part",
            {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ key, upload_id, part_number: partNumber }),
            },
            { retry: true },
        );
        if (!signRes.ok || !signRes.data) throw new Error(signRes.error || "Signing fehlgeschlagen");

        let attempt = 0;
        let etag: string | null = null;
        while (attempt < 3 && !etag) {
            if (isCancelled()) throw new Error("Abgebrochen");
            attempt++;
            try {
                const putRes = await fetch(signRes.data.url, { method: "PUT", body: chunk });
                if (!putRes.ok) throw new Error(`R2 PUT ${putRes.status}`);
                etag = putRes.headers.get("etag") || putRes.headers.get("ETag");
                if (!etag) throw new Error("R2 antwortet ohne ETag-Header — vermutlich fehlt die CORS-ExposeHeaders-Regel im Bucket.");
                etag = etag.replace(/^"|"$/g, "");
            } catch (e) {
                if (attempt >= 3) throw e;
                await new Promise((r) => setTimeout(r, 400 * attempt));
            }
        }
        parts[partNumber - 1] = { PartNumber: partNumber, ETag: etag as string };
        uploadedBytes += (end - start);
        persistProgress();
        if (performance.now() - lastReport > 250) reportProgress();
    };

    // Nur fehlende Parts einqueuen
    const queue = [] as number[];
    for (let i = 1; i <= totalParts; i++) {
        if (!doneSet.has(i)) queue.push(i);
    }
    const worker = async () => {
        while (queue.length > 0) {
            const n = queue.shift();
            if (!n) return;
            await uploadPart(n);
        }
    };

    try {
        reportProgress();
        await Promise.all(Array.from({ length: CHUNK_CONCURRENCY }, () => worker()));
        reportProgress();
        const compRes = await api<Media>(
            "/api/admin/media/multipart/complete",
            {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ media_id, key, upload_id, parts: parts.filter(Boolean) }),
            },
            // KRITISCH: Bytes sind schon bei R2. Ein 429 hier darf NICHT
            // dazu führen, dass der User die Datei erneut hochladen muss.
            // Bis zu 4 Retries mit 5s/15s/30s/60s.
            { retry: true, aggressiveRetry: true },
        );
        if (!compRes.ok || !compRes.data) throw new Error(compRes.error || "Complete fehlgeschlagen");
        removePending(upload_id);
        return compRes.data;
    } catch (e) {
        // Bei echtem Fehler (nicht bloß Cancel/Reload): Multipart abbrechen.
        // Wichtig: NICHT abbrechen, wenn Cancel gewünscht war und der Kontext
        // extern schon abgeräumt wird — abort() ist idempotent, also OK.
        if (isCancelled()) {
            try {
                await api("/api/admin/media/multipart/abort", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ key, upload_id, media_id }),
                });
            } catch { /* noop */ }
            removePending(upload_id);
        }
        throw e;
    }
}

export default function AdminMediaPage() {
    return (
        <ErrorBoundary
            testId="admin-medien-error-boundary"
            title="Der Medien-Bereich konnte nicht geladen werden."
        >
            <AdminMediaPageInner />
        </ErrorBoundary>
    );
}

function AdminMediaPageInner() {
    const [items, setItems] = useState<Media[]>([]);
    const [filterType, setFilterType] = useState("");
    // Zusätzlicher KI-Filter (rein clientseitig, kombinierbar mit typeFilter).
    const [aiFilter, setAiFilter] = useState<"all" | "ai" | "not-ai">("all");
    // Bulk-Selection: Set der ausgewählten Media-IDs.
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkPending, setBulkPending] = useState(false);
    const [error, setError] = useState("");
    const [queue, setQueue] = useState<QueueItem[]>([]);
    const [selected, setSelected] = useState<Media | null>(null);
    // Unterbrochene Multipart-Uploads (aus localStorage + gegen R2 verifiziert)
    const [pending, setPending] = useState<PendingUpload[]>([]);
    const [pendingChecked, setPendingChecked] = useState(false);
    // Uppy-Upload-Karten (getrennt vom Uppy-Dashboard — prominent oberhalb Grid)
    const [uppyUploads, setUppyUploads] = useState<UppyUploadItem[]>([]);
    // Erfolgs-Toast für abgeschlossene Uploads (~5s sichtbar)
    const [successToast, setSuccessToast] = useState<{ name: string; size: number; ts: number } | null>(null);
    const uppyRef = useRef<UppyUploaderHandle | null>(null);
    const successTimerRef = useRef<number | null>(null);
    const dropRef = useRef<HTMLDivElement | null>(null);
    const resumeFileInputRef = useRef<HTMLInputElement | null>(null);
    const activeResumeRef = useRef<PendingUpload | null>(null);

    // Refs für queue-runner und cancel-handles
    const queueRef = useRef<QueueItem[]>([]);
    queueRef.current = queue;
    const runnerActiveRef = useRef(false);
    const cancelHandlesRef = useRef<Map<string, CancelHandle>>(new Map());

    const load = useCallback(async () => {
        const url = filterType ? `/api/admin/media?media_type=${filterType}` : "/api/admin/media";
        const res = await api<{ items: Media[] }>(url);
        if (res.ok && res.data) {
            setItems(res.data.items);
            setError("");
        } else {
            setError(res.error || "Konnte Medien nicht laden.");
        }
    }, [filterType]);

    useEffect(() => { void load(); }, [load]);

    // Auto-Dismiss für den Fehler-Banner: transiente Meldungen (429/503) sollen
    // nicht dauerhaft stehen bleiben, sonst wirkt der Admin „kaputt".
    useEffect(() => {
        if (!error) return;
        const t = setTimeout(() => setError(""), 5000);
        return () => clearTimeout(t);
    }, [error]);

    /* ─── Usage-Map: „Wird verwendet in" pro Medium ─────
       Wird nach jedem Media-Refresh parallel geladen (Promise.all über N kleine Endpoints).
       Fehler pro Item werden ignoriert — schlimmster Fall: leere Anzeige. */
    const [usageMap, setUsageMap] = useState<Record<string, UsageEntry[]>>({});
    const loadUsage = useCallback(async (ids: string[]) => {
        if (ids.length === 0) { setUsageMap({}); return; }
        const results = await Promise.all(
            ids.map(async (id) => {
                const r = await api<{ usages: UsageEntry[] }>(`/api/admin/media/${id}/usage`);
                return { id, usages: r.ok && r.data ? r.data.usages : [] };
            }),
        );
        const next: Record<string, UsageEntry[]> = {};
        for (const { id, usages } of results) next[id] = usages;
        setUsageMap(next);
    }, []);
    useEffect(() => { void loadUsage(items.map((m) => m.id)); }, [items, loadUsage]);

    /* ─── Sortierung: aktive Medien (in ANY site_content referenziert) an
       oberste Position, sekundär nach dem bestehenden `items`-Order.
       „Aktiv" = mindestens eine Usage in site_content ODER als Projekt-Cover
       ODER in einer Projekt-Galerie. */
    const activeMediaIds = useMemo(() => {
        const s = new Set<string>();
        for (const [id, uses] of Object.entries(usageMap)) {
            if (uses && uses.length > 0) s.add(id);
        }
        return s;
    }, [usageMap]);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            const aActive = activeMediaIds.has(a.id) ? 0 : 1;
            const bActive = activeMediaIds.has(b.id) ? 0 : 1;
            return aActive - bActive;
        });
    }, [items, activeMediaIds]);

    /* ─── Projekt-Liste für „Verwenden als"-Modals ─────
       Einmal beim Mount laden, danach on-demand refresh nach Aktionen. */
    const [projects, setProjects] = useState<ProjectLite[]>([]);
    const loadProjects = useCallback(async () => {
        const r = await api<{ items: { id: string; slug: string; title: string }[] }>(
            "/api/admin/projects",
        );
        if (r.ok && r.data) {
            setProjects(r.data.items.map((p) => ({ id: p.id, slug: p.slug, title: p.title })));
        }
    }, []);
    useEffect(() => { void loadProjects(); }, [loadProjects]);

    /* ─── „Verwenden als"-Modal-State ─── */
    const [useAsMedia, setUseAsMedia] = useState<Media | null>(null);

    /* ─── Auto-refresh solange Items unbearbeitet sind (post-upload processing) ──
       Intervall bei 15 s: alle 5 s war zu aggressiv und konnte in Kombination
       mit dem Usage-Fanout (N × /usage-Calls) Edge-Rate-Limits (429) auslösen. */
    useEffect(() => {
        const unprocessed = items.some(
            (m) => !m.processed_at && (m.mime.startsWith("video/") || m.mime.startsWith("image/")),
        );
        if (!unprocessed) return;
        const t = setInterval(() => { void load(); }, 15000);
        return () => clearInterval(t);
    }, [items, load]);

    /* ─── Beim Mount: unterbrochene Multipart-Uploads validieren ───── */
    useEffect(() => {
        const stored = loadPending();
        if (stored.length === 0) {
            setPendingChecked(true);
            return;
        }
        void (async () => {
            const verified: PendingUpload[] = [];
            for (const p of stored) {
                const res = await api<{ active: boolean; parts: Array<{ PartNumber: number; ETag: string; Size: number }> }>(
                    `/api/admin/media/multipart/status?upload_id=${encodeURIComponent(p.uploadId)}&key=${encodeURIComponent(p.key)}`,
                );
                if (res.ok && res.data?.active) {
                    // Aktualisiere completedParts aus Server-Sicht
                    verified.push({
                        ...p,
                        completedParts: res.data.parts.map((x) => ({ PartNumber: x.PartNumber, ETag: x.ETag })),
                    });
                } else {
                    // Nicht mehr aktiv → aus localStorage entfernen
                    removePending(p.uploadId);
                }
            }
            savePending(verified);
            setPending(verified);
            setPendingChecked(true);
        })();
    }, []);

    const discardPending = useCallback(async (p: PendingUpload) => {
        try {
            await api("/api/admin/media/multipart/abort", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ key: p.key, upload_id: p.uploadId, media_id: p.mediaId }),
            });
        } catch { /* noop */ }
        removePending(p.uploadId);
        setPending((prev) => prev.filter((x) => x.uploadId !== p.uploadId));
    }, []);

    const requestResumeFile = useCallback((p: PendingUpload) => {
        activeResumeRef.current = p;
        resumeFileInputRef.current?.click();
    }, []);

    const handleResumeFileSelected = useCallback((files: FileList | null) => {
        const p = activeResumeRef.current;
        activeResumeRef.current = null;
        if (!p || !files || files.length === 0) return;
        const f = files[0];
        // Validierung: Name + Größe müssen matchen
        if (f.name !== p.filename || f.size !== p.size) {
            setError(
                `Datei stimmt nicht überein. Erwartet: ${p.filename} (${(p.size / (1024 * 1024)).toFixed(1)} MB), gewählt: ${f.name} (${(f.size / (1024 * 1024)).toFixed(1)} MB). Der Upload wurde nicht fortgesetzt.`,
            );
            return;
        }
        setError("");
        // Aus Pending-Liste entfernen und als Queue-Item mit resume-Payload einfügen
        setPending((prev) => prev.filter((x) => x.uploadId !== p.uploadId));
        const item: QueueItem = {
            id: uid(),
            file: f,
            status: "queued",
            mode: "multipart",
            uploaded: 0, speed: 0, etaSec: 0,
            resume: p,
        };
        setQueue((prev) => [...prev, item]);
        setTimeout(() => startRunner(), 0);
    }, []);

    /* ─── Queue-Runner: verarbeitet Items strikt sequenziell ─────── */
    const patchItem = useCallback((id: string, patch: Partial<QueueItem>) => {
        setQueue((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    }, []);

    const startRunner = useCallback(() => {
        if (runnerActiveRef.current) return;
        runnerActiveRef.current = true;
        void (async () => {
            while (true) {
                const next = queueRef.current.find((it) => it.status === "queued");
                if (!next) break;
                const id = next.id;
                const file = next.file;
                const handle: CancelHandle = { cancelRequested: false };
                cancelHandlesRef.current.set(id, handle);
                patchItem(id, { status: "uploading", startedAt: Date.now(), uploaded: 0 });
                try {
                    const onProgress = (loaded: number, _t: number, speed: number, etaSec: number) =>
                        patchItem(id, { uploaded: loaded, speed, etaSec });
                    if (file.size >= MULTIPART_THRESHOLD || next.resume) {
                        await uploadMultipart(
                            file,
                            onProgress,
                            (ctx) => { handle.mp = ctx; },
                            () => handle.cancelRequested,
                            next.resume,
                        );
                    } else {
                        await uploadDirect(
                            file,
                            onProgress,
                            (xhr) => { handle.xhr = xhr; },
                        );
                    }
                    if (handle.cancelRequested) {
                        patchItem(id, { status: "cancelled", error: "Abgebrochen" });
                    } else {
                        patchItem(id, { status: "done", uploaded: file.size, etaSec: 0 });
                    }
                } catch (e) {
                    const msg = (e as Error).message || "Fehler";
                    if (handle.cancelRequested || msg === "Abgebrochen") {
                        patchItem(id, { status: "cancelled", error: "Abgebrochen" });
                    } else {
                        patchItem(id, { status: "error", error: msg });
                    }
                } finally {
                    cancelHandlesRef.current.delete(id);
                }
                await load();
            }
            runnerActiveRef.current = false;
        })();
    }, [patchItem, load]);

    /* ─── Files hinzufügen (mit Kapazitäts- + MIME-Check) ─────── */
    const enqueue = useCallback((files: FileList | File[]) => {
        setError("");
        const list = Array.from(files);
        const currentPending = queueRef.current.filter(
            (q) => q.status === "queued" || q.status === "uploading",
        ).length;
        const remaining = Math.max(0, MAX_BATCH_SIZE - currentPending);
        if (list.length > remaining) {
            setError(
                `Maximal ${MAX_BATCH_SIZE} Dateien gleichzeitig in der Warteschlange. ` +
                `Aktuell ${currentPending} aktiv, ${list.length - remaining} übersprungen.`,
            );
        }
        const accepted = list.slice(0, remaining);
        const items: QueueItem[] = accepted.map((f) => {
            const invalid = !ALLOWED_MIME.has(f.type);
            return {
                id: uid(),
                file: f,
                status: invalid ? ("error" as const) : ("queued" as const),
                mode: (f.size >= MULTIPART_THRESHOLD ? "multipart" : "direct") as "direct" | "multipart",
                uploaded: 0, speed: 0, etaSec: 0,
                error: invalid ? `MIME '${f.type || "unbekannt"}' nicht erlaubt` : undefined,
            };
        });
        setQueue((prev) => [...prev, ...items]);
        // Runner nach State-Commit anstoßen
        setTimeout(() => startRunner(), 0);
    }, [startRunner]);

    /* ─── Cancel / Retry / Remove ─────── */
    const cancelItem = useCallback((id: string) => {
        const item = queueRef.current.find((q) => q.id === id);
        if (!item) return;
        if (item.status === "uploading") {
            const h = cancelHandlesRef.current.get(id);
            if (h) {
                h.cancelRequested = true;
                if (h.xhr) { try { h.xhr.abort(); } catch { /* noop */ } }
                if (h.mp) {
                    void api("/api/admin/media/multipart/abort", {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ key: h.mp.key, upload_id: h.mp.upload_id, media_id: h.mp.media_id }),
                    });
                }
            }
            patchItem(id, { status: "cancelled", error: "Abgebrochen" });
        } else {
            // queued / done / error / cancelled → aus Liste entfernen
            setQueue((prev) => prev.filter((q) => q.id !== id));
        }
    }, [patchItem]);

    const retryItem = useCallback((id: string) => {
        const item = queueRef.current.find((q) => q.id === id);
        if (!item) return;
        if (!ALLOWED_MIME.has(item.file.type)) return;
        patchItem(id, { status: "queued", uploaded: 0, speed: 0, etaSec: 0, error: undefined });
        setTimeout(() => startRunner(), 0);
    }, [patchItem, startRunner]);

    const clearFinished = useCallback(() => {
        setQueue((prev) => prev.filter((q) => q.status === "queued" || q.status === "uploading"));
    }, []);

    /* ─── Drop-Zone Handlers ─────── */
    useEffect(() => {
        const el = dropRef.current;
        if (!el) return;
        const stop = (e: DragEvent) => { e.preventDefault(); e.stopPropagation(); };
        const drop = (e: DragEvent) => {
            stop(e);
            const files = e.dataTransfer?.files;
            if (files && files.length) enqueue(files);
        };
        el.addEventListener("dragover", stop);
        el.addEventListener("dragenter", stop);
        el.addEventListener("drop", drop);
        return () => {
            el.removeEventListener("dragover", stop);
            el.removeEventListener("dragenter", stop);
            el.removeEventListener("drop", drop);
        };
    }, [enqueue]);

    /* ─── beforeunload-Warnung während aktiver Uploads ─────── */
    useEffect(() => {
        const active = queue.some((q) => q.status === "uploading" || q.status === "queued");
        if (!active) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "Uploads laufen noch — wirklich verlassen?";
            return e.returnValue;
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [queue]);

    /* ─── Media patch/delete ─────── */
    const [usageBlock, setUsageBlock] = useState<UsageBlock | null>(null);

    const remove = async (id: string) => {
        if (!window.confirm("Medium wirklich löschen?")) return;
        // Direkter fetch, weil api() bei !res.ok die JSON-Body verwirft — wir
        // brauchen den 409-Body mit `blocked`+`projects`+`sections` für die Guard-UI.
        const res = await fetch(`${BACKEND_URL}/api/admin/media/${id}`, {
            method: "DELETE",
            credentials: "include",
        });
        if (res.status === 409) {
            const body = await res.json().catch(() => ({}));
            setUsageBlock({
                mediaId: id,
                projects: body?.projects || [],
                sections: body?.sections || [],
            });
            return;
        }
        if (res.ok) { await load(); setSelected(null); }
    };

    const forceRemove = async () => {
        if (!usageBlock) return;
        const res = await api(
            `/api/admin/media/${usageBlock.mediaId}?force=true`,
            { method: "DELETE" },
        );
        if (res.ok) {
            setUsageBlock(null);
            await load();
            setSelected(null);
        }
    };

    const patch = async (id: string, p: Partial<Media>) => {
        const res = await api<Media>(`/api/admin/media/${id}`, {
            method: "PATCH", headers: { "Content-Type": "application/json" },
            body: JSON.stringify(p),
        });
        if (res.ok && res.data) {
            setItems((prev) => prev.map((m) => m.id === id ? res.data! : m));
            if (selected?.id === id) setSelected(res.data);
        }
    };

    /* ─── Bulk-KI-Kennzeichnung ─────────────────────────
       Setzt `is_ai_generated` für alle ausgewählten Medien in einem Rutsch.
       Server-Endpoint `PATCH /api/admin/media/bulk-ai` validiert IDs & Flag. */
    const patchBulk = async (flag: boolean) => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (ids.length > 50) {
            const ok = window.confirm(
                `Sicher, dass du ${ids.length} Medien ${flag ? "als KI-generiert markieren" : "die KI-Markierung entfernen"} möchtest?`,
            );
            if (!ok) return;
        }
        setBulkPending(true);
        const res = await api<{ updated: number; matched: number }>(
            "/api/admin/media/bulk-ai",
            {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids, is_ai_generated: flag }),
            },
        );
        setBulkPending(false);
        if (res.ok && res.data) {
            // Optimistic-Update im State: alle betroffenen Medien flippen.
            setItems((prev) =>
                prev.map((m) => (selectedIds.has(m.id) ? { ...m, is_ai_generated: flag } : m)),
            );
            setSelectedIds(new Set());
            toast.success(
                flag
                    ? `${res.data.updated} Medien als KI-generiert markiert.`
                    : `KI-Markierung für ${res.data.updated} Medien entfernt.`,
            );
        } else {
            toast.error(res.error || "Bulk-Aktion fehlgeschlagen.");
        }
    };

    // Reset Auswahl, wenn Nutzer den Type- oder KI-Filter wechselt.
    useEffect(() => {
        setSelectedIds(new Set());
    }, [filterType, aiFilter]);

    // Counts für die Filter-Chips (basiert auf typeFilter-gefilterten Items).
    const aiCounts = useMemo(() => {
        let ai = 0;
        for (const m of items) if (m.is_ai_generated) ai += 1;
        return { all: items.length, ai, notAi: items.length - ai };
    }, [items]);

    // Rendering-Liste = typeFilter (server-seitig) + aiFilter (client-seitig).
    const displayedItems = useMemo(() => {
        if (aiFilter === "all") return sortedItems;
        if (aiFilter === "ai") return sortedItems.filter((m) => m.is_ai_generated === true);
        return sortedItems.filter((m) => !m.is_ai_generated);
    }, [sortedItems, aiFilter]);

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const selectAllDisplayed = () => {
        setSelectedIds(new Set(displayedItems.map((m) => m.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());

    const thumbUrl = (m: Media) => mediaThumb(m, BACKEND_URL);

    /* ─── Global-Progress aggregieren ─────── */
    const activeItems = queue.filter((q) => q.status === "uploading" || q.status === "queued");
    const totalBytes = queue.reduce((sum, q) => sum + (q.status === "cancelled" || q.status === "error" ? 0 : q.file.size), 0);
    const uploadedBytes = queue.reduce((sum, q) => {
        if (q.status === "done") return sum + q.file.size;
        if (q.status === "uploading") return sum + q.uploaded;
        return sum;
    }, 0);
    const globalPct = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
    const currentIdx = queue.filter((q) => q.status !== "queued").findIndex((q) => q.status === "uploading");
    const doneCount = queue.filter((q) => q.status === "done").length;
    const totalCount = queue.length;
    const activeItem = queue.find((q) => q.status === "uploading");
    const globalSpeed = activeItem?.speed || 0;
    const remainingBytes = Math.max(0, totalBytes - uploadedBytes);
    const globalEta = globalSpeed > 0 ? remainingBytes / globalSpeed : 0;
    const hasActive = activeItems.length > 0;

    // Position der wartenden Items berechnen (nur queued zählen)
    let waitingPos = 0;
    const positionMap = new Map<string, number>();
    queue.forEach((q) => {
        if (q.status === "queued") {
            waitingPos++;
            positionMap.set(q.id, waitingPos);
        }
    });

    return (
        <div data-testid="admin-medien">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Bibliothek</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">Medien</h1>
                    <p className="mt-1 text-hf-muted">
                        Fotos, Videos und 360°-Aufnahmen. Bis {MAX_BATCH_SIZE} Dateien gleichzeitig — sequenzieller Batch-Upload nach Cloudflare&nbsp;R2.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                        className="px-3 py-2 text-xs rounded-sm bg-white border"
                        style={{ borderColor: "hsl(var(--hf-line))" }}
                        data-testid="medien-filter-type">
                        <option value="">Alle Typen</option>
                        {MEDIA_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                    </select>
                    <button onClick={() => void load()} className="hf-btn hf-btn-ghost" data-testid="medien-refresh">
                        <RefreshCw size={13} /><span>Aktualisieren</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className="mt-6 p-3 rounded text-sm flex items-start gap-3"
                    style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}
                    data-testid="medien-error"
                    role="status"
                    aria-live="polite">
                    <span className="flex-1">{error}</span>
                    <button
                        type="button"
                        onClick={() => setError("")}
                        aria-label="Meldung schließen"
                        data-testid="medien-error-dismiss"
                        className="shrink-0 opacity-70 hover:opacity-100"
                    >
                        <X size={14} aria-hidden />
                    </button>
                </div>
            )}

            {/* Unterbrochene Multipart-Uploads — Resume-Karten */}
            {pendingChecked && pending.length > 0 && (
                <div className="mt-6 space-y-3" data-testid="pending-uploads">
                    {pending.map((p) => {
                        const totalParts = Math.ceil(p.size / p.chunkSize);
                        const done = p.completedParts.length;
                        const doneBytes = p.completedParts.reduce((sum, part) => {
                            const start = (part.PartNumber - 1) * p.chunkSize;
                            const end = Math.min(start + p.chunkSize, p.size);
                            return sum + (end - start);
                        }, 0);
                        const pct = p.size > 0 ? Math.round((doneBytes / p.size) * 100) : 0;
                        return (
                            <div key={p.uploadId}
                                className="p-4 rounded-sm flex flex-wrap items-center gap-4"
                                style={{ background: "hsl(var(--hf-gold-soft) / 0.35)", border: "1px solid hsl(var(--hf-gold) / 0.55)" }}
                                data-testid={`pending-upload-${p.uploadId.slice(0, 12)}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="hf-eyebrow" style={{ color: "hsl(var(--hf-gold-dark))" }}>
                                        Unterbrochener Upload gefunden
                                    </div>
                                    <div className="mt-1 font-display text-lg truncate">{p.filename}</div>
                                    <div className="mt-1 text-xs text-hf-muted">
                                        {fmtMB(p.size)} · {done} / {totalParts} Chunks bereits übertragen ({pct}%)
                                    </div>
                                    <div className="mt-2 h-1 rounded-full overflow-hidden max-w-xs" style={{ background: "hsl(var(--hf-line))" }}>
                                        <div className="h-full" style={{ width: `${pct}%`, background: "hsl(var(--hf-gold))" }} />
                                    </div>
                                    <div className="mt-2 text-[11px] text-hf-muted">
                                        Bitte dieselbe Datei erneut auswählen, um fortzufahren. Größe und Dateiname müssen exakt übereinstimmen.
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <HFButton variant="primary" onClick={() => requestResumeFile(p)} data-testid={`pending-resume-${p.uploadId.slice(0, 12)}`}>
                                        Fortsetzen
                                    </HFButton>
                                    <HFButton variant="ghost" onClick={() => void discardPending(p)} icon={<Trash2 size={13} />} data-testid={`pending-discard-${p.uploadId.slice(0, 12)}`}>
                                        Verwerfen
                                    </HFButton>
                                </div>
                            </div>
                        );
                    })}
                    {/* Hidden input für Resume-Datei-Wahl */}
                    <input
                        ref={resumeFileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,video/mp4"
                        hidden
                        onChange={(e) => handleResumeFileSelected(e.target.files)}
                        data-testid="pending-resume-file-input"
                    />
                </div>
            )}

            <div ref={dropRef} className="mt-6" data-testid="medien-dropzone">
                <UppyUploader
                    ref={uppyRef}
                    onUploadComplete={() => void load()}
                    onUploadsChange={setUppyUploads}
                    onUploadSuccess={(item) => {
                        setSuccessToast({ name: item.name, size: item.size, ts: Date.now() });
                        if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
                        successTimerRef.current = window.setTimeout(() => setSuccessToast(null), 5500);
                    }}
                />
                <div
                    className="mt-3 flex items-start gap-2 text-xs text-hf-muted"
                    data-testid="medien-upload-hint"
                >
                    <span
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-medium mt-0.5 flex-shrink-0"
                        style={{ background: "hsl(var(--hf-gold-soft))", color: "hsl(var(--hf-ink))" }}
                        aria-hidden
                    >
                        i
                    </span>
                    <span>
                        Videos über 4K Breite werden automatisch für Web-Wiedergabe optimiert (3–5 Min. nach Upload). Status siehst du an der HLS-Pille der jeweiligen Kachel.
                    </span>
                </div>
            </div>

            {/* Erfolgs-Toast: Für 5s nach dem letzten Upload sichtbar */}
            {successToast && (
                <div
                    className="mt-4 flex items-start gap-3 px-4 py-3 rounded-sm"
                    style={{
                        background: "hsl(45 65% 96%)",
                        border: "1px solid hsl(var(--hf-gold))",
                        color: "hsl(var(--hf-ink))",
                    }}
                    data-testid="upload-success-toast"
                    role="status"
                    aria-live="polite"
                >
                    <CheckCircle2 size={18} style={{ color: "hsl(var(--hf-gold-dark))", flexShrink: 0, marginTop: 1 }} />
                    <div className="flex-1 min-w-0 text-sm">
                        <div className="font-medium">Upload erfolgreich abgeschlossen</div>
                        <div className="text-hf-muted truncate text-xs mt-0.5">
                            {successToast.name} · {fmtMB(successToast.size)} — jetzt in der Medien-Bibliothek unten sichtbar (oberste Position).
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setSuccessToast(null)}
                        aria-label="Meldung schließen"
                        className="w-6 h-6 inline-flex items-center justify-center rounded-sm hover:bg-hf-surface transition-colors"
                        data-testid="upload-success-toast-close"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* Prominente Upload-Progress-Karten (getrennt vom Uppy-Dashboard) */}
            {uppyUploads.length > 0 && (
                <div className="mt-6 space-y-3" data-testid="uppy-progress-cards">
                    <div className="flex items-baseline justify-between">
                        <div className="hf-eyebrow">Upload-Fortschritt</div>
                        <div className="text-xs text-hf-muted">
                            {uppyUploads.filter((u) => u.status === "done").length} von {uppyUploads.length} abgeschlossen
                        </div>
                    </div>
                    {uppyUploads.map((u) => {
                        const speedMBs = u.speed > 0 ? (u.speed / (1024 * 1024)).toFixed(1) : "0";
                        const isDone = u.status === "done";
                        const isErr = u.status === "error";
                        const isCancel = u.status === "uploading";
                        const barColor = isDone
                            ? "hsl(var(--hf-gold-dark))"
                            : isErr
                                ? "hsl(var(--hf-danger))"
                                : "hsl(var(--hf-gold))";
                        return (
                            <div
                                key={u.id}
                                className="p-4 rounded-sm"
                                style={{
                                    background: "hsl(var(--hf-bg))",
                                    border: "1px solid hsl(var(--hf-line))",
                                }}
                                data-testid={`uppy-progress-${u.id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 flex-shrink-0 inline-flex items-center justify-center" aria-hidden>
                                        {isDone && <CheckCircle2 size={18} style={{ color: "hsl(var(--hf-gold-dark))" }} />}
                                        {isErr && <AlertCircle size={18} style={{ color: "hsl(var(--hf-danger))" }} />}
                                        {u.status === "uploading" && (
                                            <span className="inline-block w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: "hsl(var(--hf-gold))" }} />
                                        )}
                                        {u.status === "queued" && <Clock size={16} style={{ color: "hsl(var(--hf-muted))" }} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2">
                                            <div className="font-medium text-sm truncate flex-1" title={u.name}>
                                                {u.name}
                                            </div>
                                            <div className="text-[10px] tracking-[0.14em] uppercase text-hf-muted flex-shrink-0">
                                                {fmtMB(u.size)}
                                            </div>
                                        </div>
                                        <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(var(--hf-line))" }}>
                                            <div
                                                className="h-full transition-all duration-200"
                                                style={{ width: `${u.percentage}%`, background: barColor }}
                                            />
                                        </div>
                                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-hf-muted">
                                            <span data-testid={`uppy-progress-pct-${u.id}`}>{u.percentage}%</span>
                                            <span>·</span>
                                            <span>{fmtMB(u.bytesUploaded)} / {fmtMB(u.size)}</span>
                                            {u.status === "uploading" && u.speed > 0 && (
                                                <>
                                                    <span>·</span>
                                                    <span>{speedMBs} MB/s</span>
                                                </>
                                            )}
                                            {u.status === "uploading" && u.etaSec > 0 && (
                                                <>
                                                    <span>·</span>
                                                    <span>noch {fmtEta(u.etaSec)}</span>
                                                </>
                                            )}
                                            {isDone && (
                                                <span style={{ color: "hsl(var(--hf-gold-dark))" }}>✓ verarbeitet · in Bibliothek verfügbar</span>
                                            )}
                                            {isErr && u.error && (
                                                <span style={{ color: "hsl(var(--hf-danger))" }}>{u.error}</span>
                                            )}
                                        </div>
                                    </div>
                                    {isCancel && (
                                        <button
                                            type="button"
                                            onClick={() => uppyRef.current?.cancel(u.id)}
                                            aria-label="Upload abbrechen"
                                            className="flex-shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-sm hover:bg-hf-surface transition-colors"
                                            data-testid={`uppy-progress-cancel-${u.id}`}
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* (Legacy custom multipart queue durch Uppy ersetzt — Zustand bleibt
                als Fallback vorhanden aber wird nie mehr eingereiht) */}
            {queue.length > 0 && (
                <div className="mt-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface) / 0.6)", border: "1px solid hsl(var(--hf-line))" }} data-testid="medien-queue">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div className="hf-eyebrow">Warteschlange</div>
                        <div className="flex items-center gap-2">
                            {(doneCount > 0 || queue.some((q) => q.status === "error" || q.status === "cancelled")) && !hasActive && (
                                <button onClick={clearFinished} className="text-xs text-hf-muted hover:text-hf-ink transition-colors" data-testid="queue-clear-finished">
                                    Abgeschlossene entfernen
                                </button>
                            )}
                        </div>
                    </div>
                    {hasActive && (
                        <div className="mt-3" data-testid="queue-global-progress">
                            <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
                                <div className="font-medium">
                                    Datei {currentIdx >= 0 ? currentIdx + 1 : doneCount + 1} von {totalCount}
                                </div>
                                <div className="text-hf-muted">
                                    {fmtMB(uploadedBytes)} von {fmtMB(totalBytes)}
                                    {globalSpeed > 0 && ` · ${(globalSpeed / (1024 * 1024)).toFixed(1)} MB/s`}
                                    {globalEta > 0 && ` · noch ${fmtEta(globalEta)}`}
                                </div>
                            </div>
                            <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(var(--hf-line))" }}>
                                <div className="h-full transition-all duration-200"
                                    style={{ width: `${globalPct}%`, background: "hsl(var(--hf-gold))" }} />
                            </div>
                        </div>
                    )}

                    <div className="mt-4 space-y-2">
                        {queue.map((q) => {
                            const pct = q.file.size > 0 ? Math.round((q.uploaded / q.file.size) * 100) : 0;
                            const speedMBs = q.speed > 0 ? (q.speed / (1024 * 1024)).toFixed(1) : "0";
                            const modeBadge = q.mode === "multipart" ? "CHUNKED" : "DIREKT";
                            return (
                                <div key={q.id} className="text-xs p-3 rounded-sm"
                                    style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                                    data-testid={`queue-item-${q.id}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                                            {q.status === "done" && <CheckCircle2 size={16} style={{ color: "hsl(var(--hf-gold-dark))" }} />}
                                            {q.status === "error" && <AlertCircle size={16} style={{ color: "hsl(var(--hf-danger))" }} />}
                                            {q.status === "cancelled" && <X size={16} style={{ color: "hsl(var(--hf-muted))" }} />}
                                            {q.status === "queued" && <Clock size={14} style={{ color: "hsl(var(--hf-muted))" }} />}
                                            {q.status === "uploading" && (
                                                <span className="inline-block w-2 h-2 rounded-full animate-pulse"
                                                    style={{ background: "hsl(var(--hf-gold))" }} />
                                            )}
                                        </div>
                                        <span className="truncate flex-1 font-medium">{q.file.name}</span>
                                        <span className="text-[10px] uppercase tracking-[0.14em] text-hf-muted">{modeBadge}</span>
                                        <span className="text-[10px] text-hf-muted">{fmtMB(q.file.size)}</span>
                                        <div className="flex items-center gap-1">
                                            {q.status === "error" && ALLOWED_MIME.has(q.file.type) && (
                                                <button onClick={() => retryItem(q.id)}
                                                    aria-label="Erneut versuchen"
                                                    className="w-6 h-6 inline-flex items-center justify-center rounded-sm hover:bg-hf-surface transition-colors"
                                                    data-testid={`queue-retry-${q.id}`}>
                                                    <RotateCcw size={13} />
                                                </button>
                                            )}
                                            <button onClick={() => cancelItem(q.id)}
                                                aria-label={q.status === "uploading" ? "Upload abbrechen" : "Aus Liste entfernen"}
                                                className="w-6 h-6 inline-flex items-center justify-center rounded-sm hover:bg-hf-surface transition-colors"
                                                data-testid={`queue-cancel-${q.id}`}>
                                                <X size={13} />
                                            </button>
                                        </div>
                                    </div>

                                    {q.status === "uploading" && (
                                        <div className="mt-2">
                                            <div className="h-1 rounded-full overflow-hidden" style={{ background: "hsl(var(--hf-line))" }}>
                                                <div className="h-full transition-all duration-200"
                                                    style={{ width: `${pct}%`, background: "hsl(var(--hf-gold))" }} />
                                            </div>
                                            <div className="mt-1 text-[10px] text-hf-muted flex items-center gap-3">
                                                <span>{pct}%</span>
                                                <span>{fmtMB(q.uploaded)} / {fmtMB(q.file.size)}</span>
                                                <span>· {speedMBs} MB/s</span>
                                                {q.etaSec > 0 && <span>· noch {fmtEta(q.etaSec)}</span>}
                                            </div>
                                        </div>
                                    )}

                                    {q.status === "queued" && (
                                        <div className="mt-1 text-[10px] text-hf-muted">
                                            In Warteschlange (Position {positionMap.get(q.id) || "?"})
                                        </div>
                                    )}
                                    {q.status === "done" && (
                                        <div className="mt-1 text-[10px]" style={{ color: "hsl(var(--hf-gold-dark))" }}>
                                            ✓ Upload abgeschlossen
                                        </div>
                                    )}
                                    {(q.status === "error" || q.status === "cancelled") && q.error && (
                                        <div className="mt-1 text-[10px]" style={{ color: q.status === "error" ? "hsl(var(--hf-danger))" : "hsl(var(--hf-muted))" }}>
                                            {q.error}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Filter-Chip-Bar (KI-Filter) + Auswahl-Aktionen */}
            <div
                className="mt-8 flex flex-wrap items-center gap-2"
                data-testid="medien-ai-filter-bar"
            >
                {([
                    { key: "all", label: "Alle", count: aiCounts.all },
                    { key: "ai", label: "Nur KI-markiert", count: aiCounts.ai },
                    { key: "not-ai", label: "Nicht markiert", count: aiCounts.notAi },
                ] as const).map((opt) => {
                    const active = aiFilter === opt.key;
                    return (
                        <button
                            key={opt.key}
                            type="button"
                            onClick={() => setAiFilter(opt.key)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors"
                            style={{
                                background: active ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-surface))",
                                color: active ? "hsl(var(--hf-bg))" : "hsl(var(--hf-ink))",
                                border: active ? "1px solid hsl(var(--hf-gold-dark))" : "1px solid hsl(var(--hf-line))",
                                fontWeight: active ? 600 : 500,
                            }}
                            data-testid={`medien-ai-filter-${opt.key}`}
                            aria-pressed={active}
                        >
                            {opt.key === "ai" && (
                                <Sparkles size={12} strokeWidth={2} aria-hidden />
                            )}
                            <span>{opt.label}</span>
                            <span
                                className="tabular-nums"
                                style={{
                                    fontSize: 11,
                                    opacity: 0.75,
                                    marginLeft: 2,
                                }}
                            >
                                ({opt.count})
                            </span>
                        </button>
                    );
                })}
                <div className="flex-1" />
                {displayedItems.length > 0 && (
                    <button
                        type="button"
                        onClick={() => {
                            if (selectedIds.size === displayedItems.length) clearSelection();
                            else selectAllDisplayed();
                        }}
                        className="text-xs text-hf-muted hover:text-hf-ink transition-colors"
                        data-testid="medien-select-all-toggle"
                    >
                        {selectedIds.size === displayedItems.length && selectedIds.size > 0
                            ? "Auswahl aufheben"
                            : `Alle auswählen (${displayedItems.length})`}
                    </button>
                )}
            </div>


            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="medien-grid">
                {items.length === 0 && (
                    <div className="col-span-full text-sm text-hf-muted py-6 text-center">
                        Noch keine Medien hochgeladen.
                    </div>
                )}
                {displayedItems.length === 0 && items.length > 0 && (
                    <div className="col-span-full text-sm text-hf-muted py-6 text-center">
                        Keine Medien entsprechen dem aktuellen Filter.
                    </div>
                )}
                {displayedItems.map((m) => {
                    const t = thumbUrl(m);
                    const usages = usageMap[m.id];
                    const isActive = activeMediaIds.has(m.id);
                    const isSelected = selectedIds.has(m.id);
                    const isAi = m.is_ai_generated === true;
                    return (
                    <div key={m.id} className="flex flex-col relative group/card" data-testid={`medien-cell-${m.id}`}>
                    {/* Multi-Select-Checkbox (oben-links, Overlay über der Kachel) —
                        sichtbar bei Hover oder wenn schon ausgewählt/eine andere Auswahl aktiv. */}
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleSelect(m.id); }}
                        aria-label={isSelected ? "Auswahl entfernen" : "Zur Auswahl hinzufügen"}
                        aria-pressed={isSelected}
                        data-testid={`medien-select-${m.id}`}
                        className={
                            "absolute z-20 top-2 left-2 inline-flex items-center justify-center rounded-sm transition-opacity " +
                            (isSelected || selectedIds.size > 0
                                ? "opacity-100"
                                : "opacity-0 group-hover/card:opacity-100 focus:opacity-100")
                        }
                        style={{
                            width: 24,
                            height: 24,
                            background: isSelected ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-bg) / 0.92)",
                            border: isSelected
                                ? "1px solid hsl(var(--hf-gold-dark))"
                                : "1px solid hsl(var(--hf-line))",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            color: isSelected ? "hsl(var(--hf-bg))" : "hsl(var(--hf-ink))",
                        }}
                    >
                        {isSelected ? <CheckSquare size={14} strokeWidth={2.4} /> : <Square size={14} strokeWidth={2} />}
                    </button>
                    {/* Dezenter ✨-KI-Indikator auf der Kachel — sichtbar sobald
                        `is_ai_generated=true`. Klein, unauffällig, nur für den Admin-Überblick. */}
                    {isAi && (
                        <span
                            className="absolute z-10 inline-flex items-center justify-center rounded-full pointer-events-none"
                            data-testid={`medien-ai-indicator-${m.id}`}
                            aria-label="KI-generiert"
                            style={{
                                top: 6,
                                right: 6,
                                width: 20,
                                height: 20,
                                background: "rgba(23, 22, 19, 0.75)",
                                color: "#EBD5A0",
                                border: "1px solid rgba(215, 170, 100, 0.35)",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                            }}
                        >
                            <Sparkles size={11} strokeWidth={2} />
                        </span>
                    )}
                    <button onClick={() => setSelected(m)} type="button"
                        className="text-left group relative aspect-[4/3] overflow-hidden rounded-sm"
                        style={{
                            background: "hsl(var(--hf-surface))",
                            border: isSelected
                                ? "2px solid hsl(var(--hf-gold-dark))"
                                : "1px solid hsl(var(--hf-line))",
                        }}
                        data-testid={`medien-item-${m.id}`}>
                        {t ? (
                            <img src={t} alt={m.alt || m.title}
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: "hsl(var(--hf-surface))" }}>
                                <Video size={28} style={{ color: "hsl(var(--hf-muted))" }} />
                            </div>
                        )}
                        <span className="absolute top-2 left-2 text-[9px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-sm"
                            style={{ background: "hsl(var(--hf-ink) / 0.78)", color: "hsl(var(--hf-gold-soft))" }}>
                            {TYPE_LABEL[m.media_type] || m.media_type}
                        </span>
                        {m.is_demo && (
                            <span
                                className="absolute top-8 left-2 text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-sm"
                                style={{ background: "hsl(45 90% 60%)", color: "hsl(var(--hf-ink))" }}
                                data-testid={`medien-demo-badge-${m.id}`}
                            >
                                Demo
                            </span>
                        )}
                        {m.mime.startsWith("video/") && (
                            <span className="absolute bottom-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full"
                                style={{ background: "hsl(var(--hf-gold))", color: "hsl(var(--hf-ink))" }}>
                                <Video size={13} />
                            </span>
                        )}
                        {m.mime.startsWith("video/") && m.processed_at && (() => {
                            // HLS-Status-Pille (linker Rand, über der „verborgen"-Pille wenn beide da):
                            //   • hls_ready       → grün „HLS bereit"
                            //   • width > 4096    → gelb „wird optimiert"
                            //   • sonst           → grau „Direkt-Stream"
                            const oversized = (m.width || 0) > 4096;
                            const posY = !m.visible ? "bottom-9" : "bottom-2";
                            if (m.hls_ready) {
                                return (
                                    <span
                                        className={`absolute ${posY} left-2 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm`}
                                        style={{ background: "hsl(140 45% 40%)", color: "white" }}
                                        data-testid={`medien-hls-card-ready-${m.id}`}
                                    >
                                        HLS bereit
                                    </span>
                                );
                            }
                            if (oversized) {
                                return (
                                    <span
                                        className={`absolute ${posY} left-2 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm animate-pulse`}
                                        style={{ background: "hsl(38 90% 55%)", color: "hsl(var(--hf-ink))" }}
                                        data-testid={`medien-hls-card-pending-${m.id}`}
                                    >
                                        wird optimiert
                                    </span>
                                );
                            }
                            return (
                                <span
                                    className={`absolute ${posY} left-2 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm`}
                                    style={{ background: "hsl(var(--hf-surface))", color: "hsl(var(--hf-muted))", border: "1px solid hsl(var(--hf-line))" }}
                                    data-testid={`medien-hls-card-direct-${m.id}`}
                                >
                                    Direkt-Stream
                                </span>
                            );
                        })()}
                        {!m.processed_at && (
                            <span
                                className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm animate-pulse"
                                style={{ background: "hsl(var(--hf-gold-soft) / 0.85)", color: "hsl(var(--hf-gold-dark))" }}
                                data-testid={`medien-processing-${m.id}`}
                            >
                                wird verarbeitet
                            </span>
                        )}
                        {!m.visible && (
                            <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm"
                                style={{ background: "hsl(var(--hf-danger) / 0.85)", color: "white" }}>
                                verborgen
                            </span>
                        )}
                        {isActive && (
                            <span
                                className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-sm font-medium"
                                style={{
                                    background: "hsl(140 55% 42%)",
                                    color: "white",
                                    boxShadow: "0 1px 3px hsl(var(--hf-ink) / 0.35)",
                                }}
                                data-testid={`medien-active-badge-${m.id}`}
                                title={
                                    usages && usages.length > 0
                                        ? "Aktiv in: " + usages.map((u) =>
                                            u.type === "site_content"
                                                ? (u.section_title || u.section_key)
                                                : (u.project_title || u.project_slug || "Projekt")
                                          ).join(" · ")
                                        : "Aktiv"
                                }
                            >
                                Aktiv
                            </span>
                        )}
                    </button>
                    {/* Delete-Button — Sibling des Card-Buttons, absolut positioniert
                        im .group/card wrapper (position: relative). Der Ring auf der
                        Karte + Hover-Effekt kommen weiter über das große <button>.
                        remove() nutzt den bestehenden confirm+409-Flow. */}
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); void remove(m.id); }}
                        aria-label="Löschen"
                        title="Medium löschen"
                        data-testid={`medien-card-delete-${m.id}`}
                        className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-sm z-20 transition-all opacity-0 group-hover/card:opacity-100 hover:!opacity-100"
                        style={{
                            background: "hsl(var(--hf-ink) / 0.85)",
                            color: "hsl(var(--hf-danger))",
                            border: "1px solid hsl(var(--hf-line))",
                            // Wenn Aktiv-Badge sichtbar → 32px runter, damit sie nicht überlappen
                            transform: isActive ? "translateY(28px)" : "translateY(0)",
                        }}
                    >
                        <Trash2 size={13} />
                    </button>
                    {/* Usage-Footer + „Verwenden als"-Button */}
                    <div className="mt-2 flex items-start gap-2">
                        <div className="flex-1 min-w-0 text-[11px] leading-snug" data-testid={`medien-usage-${m.id}`}>
                            <div className="hf-eyebrow text-[9px]" style={{ color: "hsl(var(--hf-muted))" }}>Wird verwendet in</div>
                            {usages === undefined && <div className="text-hf-muted italic">…</div>}
                            {usages !== undefined && usages.length === 0 && (
                                <div className="text-hf-muted italic" data-testid={`medien-usage-none-${m.id}`}>Nicht zugewiesen</div>
                            )}
                            {usages !== undefined && usages.length > 0 && (
                                <UsageChips usages={usages} />
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => setUseAsMedia(m)}
                            data-testid={`medien-use-as-${m.id}`}
                            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] tracking-wide transition-colors"
                            style={{
                                border: "1px solid hsl(var(--hf-gold) / 0.55)",
                                background: "hsl(var(--hf-gold-soft) / 0.55)",
                                color: "hsl(var(--hf-gold-dark))",
                            }}
                            aria-label="Verwenden als"
                            title="Verwenden als..."
                        >
                            <Sparkles size={12} />
                            <span>Verwenden als</span>
                        </button>
                    </div>
                    </div>
                    );
                })}
            </div>

            {useAsMedia && (
                <UseAsModal
                    media={useAsMedia}
                    projects={projects}
                    onClose={() => setUseAsMedia(null)}
                    onSaved={async () => {
                        setUseAsMedia(null);
                        await loadUsage(items.map((m) => m.id));
                    }}
                />
            )}

            {selected && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6"
                    style={{ background: "rgba(14,23,38,0.75)" }} onClick={() => setSelected(null)}>
                    <div className="w-full max-w-2xl rounded-sm overflow-hidden"
                        style={{ background: "hsl(var(--hf-bg))" }} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "hsl(var(--hf-line))" }}>
                            <div className="font-display text-lg">Medium bearbeiten</div>
                            <button onClick={() => setSelected(null)} aria-label="Schließen"
                                className="w-9 h-9 inline-flex items-center justify-center border border-hf-line rounded-sm">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                            <img src={thumbUrl(selected) || `${BACKEND_URL}/api/media/${selected.id}/poster`} alt={selected.alt}
                                className="w-full aspect-[4/3] object-cover rounded-sm bg-hf-dark" />
                            <div className="space-y-3 text-sm">
                                <div>
                                    <label className="hf-eyebrow" style={{ fontSize: 10 }}>Titel</label>
                                    <input value={selected.title} onChange={(e) => setSelected({ ...selected, title: e.target.value })}
                                        onBlur={(e) => void patch(selected.id, { title: e.target.value })}
                                        className="w-full mt-1 px-3 py-2 border rounded-sm bg-white" style={{ borderColor: "hsl(var(--hf-line))" }}
                                        data-testid="medien-edit-title" />
                                </div>
                                <div>
                                    <label className="hf-eyebrow" style={{ fontSize: 10 }}>Alt-Text</label>
                                    <input value={selected.alt} onChange={(e) => setSelected({ ...selected, alt: e.target.value })}
                                        onBlur={(e) => void patch(selected.id, { alt: e.target.value })}
                                        className="w-full mt-1 px-3 py-2 border rounded-sm bg-white" style={{ borderColor: "hsl(var(--hf-line))" }} />
                                </div>
                                <div>
                                    <label className="hf-eyebrow" style={{ fontSize: 10 }}>Beschreibung</label>
                                    <textarea value={selected.description} rows={3}
                                        onChange={(e) => setSelected({ ...selected, description: e.target.value })}
                                        onBlur={(e) => void patch(selected.id, { description: e.target.value })}
                                        className="w-full mt-1 px-3 py-2 border rounded-sm bg-white" style={{ borderColor: "hsl(var(--hf-line))" }} />
                                </div>
                                <div>
                                    <label className="hf-eyebrow" style={{ fontSize: 10 }}>Typ</label>
                                    <select value={selected.media_type}
                                        onChange={(e) => { setSelected({ ...selected, media_type: e.target.value }); void patch(selected.id, { media_type: e.target.value }); }}
                                        className="w-full mt-1 px-3 py-2 border rounded-sm bg-white" style={{ borderColor: "hsl(var(--hf-line))" }}
                                        data-testid="medien-edit-type">
                                        {MEDIA_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                                    </select>
                                </div>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={selected.visible}
                                        onChange={(e) => { setSelected({ ...selected, visible: e.target.checked }); void patch(selected.id, { visible: e.target.checked }); }} />
                                    <span>Sichtbar</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={selected.download_allowed}
                                        onChange={(e) => { setSelected({ ...selected, download_allowed: e.target.checked }); void patch(selected.id, { download_allowed: e.target.checked }); }} />
                                    <span>Download erlaubt</span>
                                </label>
                                <label
                                    className="flex items-start gap-2"
                                    data-testid="medien-edit-ai-toggle-label"
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!selected.is_ai_generated}
                                        onChange={(e) => {
                                            const val = e.target.checked;
                                            setSelected({ ...selected, is_ai_generated: val });
                                            void patch(selected.id, { is_ai_generated: val });
                                        }}
                                        data-testid="medien-edit-ai-toggle"
                                    />
                                    <span>
                                        KI-generiertes Bild kennzeichnen
                                        {selected.is_ai_generated && selected.source && (
                                            <span className="block text-xs text-hf-muted mt-0.5">
                                                Automatisch gesetzt (KI-Generator-Output: {selected.source})
                                            </span>
                                        )}
                                    </span>
                                </label>
                                {selected.download_allowed && (
                                    <a
                                        href={`${BACKEND_URL}/api/media/${selected.id}`}
                                        download={selected.original_filename || `${selected.id}${selected.mime === "image/png" ? ".png" : ""}`}
                                        className="inline-flex items-center gap-2 self-start mt-1 px-3 py-2 rounded-sm text-xs font-medium tracking-wide transition-colors"
                                        style={{ background: "hsl(var(--hf-gold))", color: "hsl(var(--hf-ink))" }}
                                        data-testid="medien-edit-download-button"
                                        title="Originaldatei herunterladen"
                                    >
                                        <span aria-hidden>↓</span>
                                        <span>Datei herunterladen</span>
                                    </a>
                                )}

                                {/* Vorher/Nachher-Paar (nur bei Fotos sinnvoll) */}
                                {selected.mime.startsWith("image/") && (
                                    <div className="pt-3 mt-2 border-t" style={{ borderColor: "hsl(var(--hf-line))" }} data-testid="medien-edit-pair-section">
                                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Vorher/Nachher-Paar</div>
                                        <div className="mt-2 grid grid-cols-2 gap-2">
                                            <select
                                                value={selected.pair_role || ""}
                                                onChange={(e) => {
                                                    const v = e.target.value as "" | "before" | "after";
                                                    const next = v === "" ? null : v;
                                                    setSelected({ ...selected, pair_role: next });
                                                    void patch(selected.id, { pair_role: next });
                                                }}
                                                className="px-2 py-1.5 border rounded-sm bg-white text-xs"
                                                style={{ borderColor: "hsl(var(--hf-line))" }}
                                                data-testid="medien-edit-pair-role"
                                            >
                                                <option value="">— Keine Rolle —</option>
                                                <option value="before">Vorher</option>
                                                <option value="after">Nachher</option>
                                            </select>
                                            <div className="flex gap-1">
                                                <input
                                                    type="text"
                                                    value={selected.pair_id || ""}
                                                    placeholder="Paar-ID"
                                                    onChange={(e) => setSelected({ ...selected, pair_id: e.target.value })}
                                                    onBlur={(e) => void patch(selected.id, { pair_id: e.target.value })}
                                                    className="flex-1 px-2 py-1.5 border rounded-sm bg-white text-xs font-mono"
                                                    style={{ borderColor: "hsl(var(--hf-line))" }}
                                                    data-testid="medien-edit-pair-id"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const pid = `pair_${Math.random().toString(36).slice(2, 10)}`;
                                                        setSelected({ ...selected, pair_id: pid });
                                                        void patch(selected.id, { pair_id: pid });
                                                    }}
                                                    className="px-2 py-1.5 rounded-sm text-xs"
                                                    style={{ background: "hsl(var(--hf-gold))", color: "hsl(var(--hf-ink))" }}
                                                    title="Neue Paar-ID generieren"
                                                    data-testid="medien-edit-pair-gen"
                                                >
                                                    Neu
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-hf-muted mt-1.5 leading-relaxed">
                                            Ordnen Sie zwei Bildern (Vorher + Nachher) dieselbe Paar-ID zu — sie werden dann automatisch mit dem Slider gezeigt.
                                        </p>
                                    </div>
                                )}

                                {/* HLS-Status (nur Videos) */}
                                {selected.mime.startsWith("video/") && (
                                    <div className="pt-3 mt-2 border-t" style={{ borderColor: "hsl(var(--hf-line))" }} data-testid="medien-edit-hls-section">
                                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>HLS-Streaming</div>
                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                            {selected.hls_ready ? (
                                                <span
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.14em] uppercase rounded-sm"
                                                    style={{ background: "hsl(var(--hf-gold-soft))", color: "hsl(var(--hf-ink))", border: "1px solid hsl(var(--hf-gold))" }}
                                                    data-testid="medien-hls-badge-ready"
                                                >
                                                    HLS ready ✓
                                                </span>
                                            ) : (
                                                <span
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.14em] uppercase rounded-sm"
                                                    style={{ background: "hsl(var(--hf-surface))", color: "hsl(var(--hf-muted))", border: "1px solid hsl(var(--hf-line))" }}
                                                    data-testid="medien-hls-badge-pending"
                                                >
                                                    HLS pending
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    const r = await api(`/api/admin/media/${selected.id}/hls-transcode`, { method: "POST" });
                                                    if (r.ok) {
                                                        window.alert("HLS-Transcode gestartet — läuft im Hintergrund.");
                                                    } else {
                                                        window.alert("Fehler: " + (r.error || "unbekannt"));
                                                    }
                                                }}
                                                className="px-2.5 py-1.5 rounded-sm text-xs"
                                                style={{ background: "hsl(var(--hf-ink))", color: "hsl(var(--hf-bg))" }}
                                                data-testid="medien-hls-retranscode"
                                            >
                                                HLS neu erzeugen
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="text-xs text-hf-muted">
                                    {selected.width}×{selected.height} · {(((selected.size ?? selected.size_bytes ?? 0)) / (1024 * 1024)).toFixed(2)} MB · {selected.mime}
                                    {selected.stored_url && <div className="mt-1 truncate">R2: <a href={selected.stored_url} target="_blank" rel="noreferrer" className="underline">{selected.stored_url.replace(/^https?:\/\/[^/]+\//, "")}</a></div>}
                                    {selected.captured_at && <div>Aufgenommen: {fmtDateTime(selected.captured_at)}</div>}
                                </div>
                                <HFButton variant="ghost" onClick={() => void remove(selected.id)} icon={<Trash2 size={13} />} data-testid="medien-delete">
                                    Löschen
                                </HFButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Usage-Block-Modal: 409 Conflict beim Löschen */}
            {usageBlock && (
                <div
                    className="fixed inset-0 z-[90] flex items-center justify-center px-4"
                    style={{ background: "hsl(var(--hf-ink) / 0.72)" }}
                    onClick={() => setUsageBlock(null)}
                    data-testid="medien-usage-block-modal"
                >
                    <div
                        className="max-w-lg w-full rounded-sm p-6"
                        style={{ background: "hsl(var(--hf-paper))", border: "1px solid hsl(var(--hf-line))" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="font-display text-xl mb-2">Medium wird noch verwendet</h3>
                        <p className="text-sm text-hf-muted mb-4">
                            Dieses Medium ist in <strong>{usageBlock.projects.length + (usageBlock.sections?.length || 0)}</strong> Stelle{(usageBlock.projects.length + (usageBlock.sections?.length || 0)) === 1 ? "" : "n"} eingebunden. Bitte erst dort entfernen — oder trotzdem löschen (Projekt-Referenzen werden automatisch ausgetragen; Startseiten-/Sylt-Sektionen bleiben mit einem fehlenden Verweis zurück und müssen manuell im Admin nachgezogen werden).
                        </p>
                        <ul className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                            {usageBlock.projects.map((p, idx) => (
                                <li
                                    key={`p-${p.slug}-${idx}`}
                                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm"
                                    style={{ background: "hsl(var(--hf-surface))" }}
                                >
                                    <Link
                                        to={`/projekt/${p.slug}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sm underline"
                                    >
                                        {p.title}
                                    </Link>
                                    <span
                                        className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                        style={{
                                            background: p.usage === "cover"
                                                ? "hsl(var(--hf-gold))"
                                                : "hsl(var(--hf-line))",
                                            color: p.usage === "cover"
                                                ? "hsl(var(--hf-ink))"
                                                : "hsl(var(--hf-muted))",
                                        }}
                                    >
                                        {p.usage === "cover" ? "Cover" : "Galerie"}
                                    </span>
                                </li>
                            ))}
                            {(usageBlock.sections || []).map((s, idx) => (
                                <li
                                    key={`s-${s.section_key}-${idx}`}
                                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm"
                                    style={{ background: "hsl(var(--hf-surface))" }}
                                    data-testid={`medien-usage-section-${s.section_key}`}
                                >
                                    <span className="text-sm">{s.section_title}</span>
                                    <span
                                        className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                        style={{
                                            background: "hsl(140 55% 42%)",
                                            color: "white",
                                        }}
                                    >
                                        CMS
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                            <HFButton
                                variant="ghost"
                                onClick={() => setUsageBlock(null)}
                                data-testid="medien-usage-block-cancel"
                            >
                                Abbrechen
                            </HFButton>
                            <button
                                type="button"
                                onClick={() => void forceRemove()}
                                className="hf-btn"
                                style={{
                                    background: "hsl(var(--hf-danger))",
                                    color: "white",
                                    border: "1px solid hsl(var(--hf-danger))",
                                }}
                                data-testid="medien-usage-block-force"
                            >
                                Trotzdem löschen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Bulk-Aktionsleiste — sichtbar sobald ≥1 Medium ausgewählt ist. */}
            {selectedIds.size > 0 && (
                <div
                    className="fixed left-1/2 -translate-x-1/2 z-40 flex flex-wrap items-center gap-2 rounded-full"
                    data-testid="medien-bulk-bar"
                    style={{
                        bottom: 20,
                        background: "hsl(var(--hf-ink))",
                        color: "hsl(var(--hf-bg))",
                        padding: "8px 10px 8px 16px",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.28), 0 3px 8px rgba(0,0,0,0.16)",
                        border: "1px solid hsl(var(--hf-gold-dark) / 0.35)",
                        maxWidth: "calc(100vw - 24px)",
                    }}
                    role="region"
                    aria-label="Bulk-Aktionen für ausgewählte Medien"
                >
                    <span
                        className="text-sm"
                        style={{ letterSpacing: "0.02em" }}
                        data-testid="medien-bulk-count"
                    >
                        <strong className="font-semibold">{selectedIds.size}</strong>{" "}
                        {selectedIds.size === 1 ? "Medium" : "Medien"} ausgewählt
                    </span>
                    <span
                        aria-hidden
                        style={{
                            width: 1,
                            height: 22,
                            background: "hsl(var(--hf-bg) / 0.2)",
                            marginInline: 6,
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => void patchBulk(true)}
                        disabled={bulkPending}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
                        style={{
                            background: "hsl(var(--hf-gold))",
                            color: "hsl(var(--hf-ink))",
                            opacity: bulkPending ? 0.5 : 1,
                            cursor: bulkPending ? "wait" : "pointer",
                        }}
                        data-testid="medien-bulk-mark-ai"
                    >
                        <Sparkles size={12} strokeWidth={2.2} />
                        Als KI-generiert markieren
                    </button>
                    <button
                        type="button"
                        onClick={() => void patchBulk(false)}
                        disabled={bulkPending}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                        style={{
                            background: "transparent",
                            color: "hsl(var(--hf-bg))",
                            border: "1px solid hsl(var(--hf-bg) / 0.3)",
                            opacity: bulkPending ? 0.5 : 1,
                            cursor: bulkPending ? "wait" : "pointer",
                        }}
                        data-testid="medien-bulk-unmark-ai"
                    >
                        KI-Markierung entfernen
                    </button>
                    <button
                        type="button"
                        onClick={clearSelection}
                        disabled={bulkPending}
                        aria-label="Auswahl leeren"
                        className="inline-flex items-center justify-center rounded-full transition-colors"
                        style={{
                            width: 28,
                            height: 28,
                            background: "transparent",
                            color: "hsl(var(--hf-bg))",
                            border: "1px solid hsl(var(--hf-bg) / 0.2)",
                            opacity: bulkPending ? 0.5 : 1,
                        }}
                        data-testid="medien-bulk-cancel"
                    >
                        <X size={14} strokeWidth={2} />
                    </button>
                </div>
            )}
        </div>
    );
}

/* ─────────────  Usage-Chips + „Verwenden als"-Modal  ───────────── */

const USAGE_LABEL: Record<UsageEntry["type"], string> = {
    cover: "Cover",
    gallery: "Galerie",
    pair_before: "Vorher",
    pair_after: "Nachher",
    site_content: "CMS",
};

function UsageChips({ usages }: { usages: UsageEntry[] }) {
    // Kompakte Chip-Liste; identische Kombis werden nicht dedupliziert (nützlich
    // um zu sehen, wenn dieselbe Media z.B. sowohl Cover als auch in der Galerie ist).
    return (
        <div className="mt-1 flex flex-wrap gap-1">
            {usages.map((u, i) => {
                const isCms = u.type === "site_content";
                const label = USAGE_LABEL[u.type];
                const displayTitle = isCms
                    ? (u.section_title || u.section_key || "Sektion")
                    : (u.project_title || u.project_slug || "Projekt");
                const href = isCms
                    ? "/admin/startseite"
                    : `/admin/projekte?edit=${u.project_id || ""}`;
                const key = isCms
                    ? `sc-${u.section_key}-${i}`
                    : `pr-${u.project_id}-${u.type}-${i}`;
                const testId = isCms
                    ? `usage-chip-cms-${u.section_key}`
                    : `usage-chip-${u.project_slug}-${u.type}`;
                return (
                    <a
                        key={key}
                        href={href}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] transition-colors hover:opacity-80"
                        style={{
                            background: isCms
                                ? "hsl(140 55% 42% / 0.18)"
                                : "hsl(var(--hf-gold-soft) / 0.55)",
                            color: isCms
                                ? "hsl(140 55% 24%)"
                                : "hsl(var(--hf-gold-dark))",
                            border: `1px solid ${isCms ? "hsl(140 55% 42% / 0.4)" : "hsl(var(--hf-gold) / 0.35)"}`,
                        }}
                        title={`${label}${u.pair_title ? ` · ${u.pair_title}` : ""} → ${displayTitle}`}
                        data-testid={testId}
                    >
                        <span className="font-semibold">{label}</span>
                        <span className="opacity-70">·</span>
                        <span className="truncate max-w-[140px]">{displayTitle}</span>
                    </a>
                );
            })}
        </div>
    );
}

type UseAsAction = "add_to_gallery" | "set_as_cover" | "set_as_pair";

function UseAsModal({
    media,
    projects,
    onClose,
    onSaved,
}: {
    media: Media;
    projects: ProjectLite[];
    onClose: () => void;
    onSaved: () => Promise<void>;
}) {
    const [action, setAction] = useState<UseAsAction>("add_to_gallery");
    const [projectId, setProjectId] = useState<string>("");
    const [pairRole, setPairRole] = useState<"before" | "after">("before");
    const [pairTitle, setPairTitle] = useState<string>("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isPhotoish = media.media_type === "photo" || media.media_type === "photo_360";

    const submit = async () => {
        setError(null);
        if (!projectId) { setError("Bitte ein Projekt wählen."); return; }
        setSaving(true);
        try {
            if (action === "add_to_gallery") {
                const proj = await api<{ media_ids?: string[] }>(`/api/admin/projects/${projectId}`);
                const existing = (proj.ok && proj.data?.media_ids) || [];
                if (existing.includes(media.id)) {
                    setError("Diese Media ist bereits in der Galerie dieses Projekts.");
                    setSaving(false); return;
                }
                const r = await api(`/api/admin/projects/${projectId}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ media_ids: [...existing, media.id] }),
                });
                if (!r.ok) throw new Error(r.error || "PATCH fehlgeschlagen");
            } else if (action === "set_as_cover") {
                const r = await api(`/api/admin/projects/${projectId}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ cover_media_id: media.id }),
                });
                if (!r.ok) throw new Error(r.error || "PATCH fehlgeschlagen");
            } else if (action === "set_as_pair") {
                if (!isPhotoish) { setError("Nur Fotos können als Vorher/Nachher genutzt werden."); setSaving(false); return; }
                // Neues Paar an das Projekt anhängen — nur die eigene Rolle ist gesetzt,
                // die andere bleibt leer und kann später im PairsEditor gefüllt werden.
                const body: Record<string, unknown> = {
                    title: pairTitle || (pairRole === "before" ? "Vorher" : "Nachher"),
                };
                body[pairRole === "before" ? "before_media_id" : "after_media_id"] = media.id;
                const r = await api(`/api/admin/projects/${projectId}/pairs`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                });
                if (!r.ok) throw new Error(r.error || "Pair-Anlage fehlgeschlagen");
            }
            await onSaved();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4"
            style={{ background: "rgba(14,23,38,0.72)", backdropFilter: "blur(4px)" }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="medien-use-as-modal"
        >
            <div
                className="w-full max-w-[520px] rounded-sm p-5"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="hf-eyebrow text-[10px]" style={{ color: "hsl(var(--hf-gold-dark))" }}>
                            Verwenden als
                        </div>
                        <div className="font-display text-lg truncate">{media.title || media.original_filename}</div>
                        <div className="text-[10px] text-hf-muted mt-0.5">{TYPE_LABEL[media.media_type] || media.media_type}</div>
                    </div>
                    <button type="button" onClick={onClose}
                        data-testid="medien-use-as-close"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-sm"
                        style={{ border: "1px solid hsl(var(--hf-line))", background: "hsl(var(--hf-surface))" }}
                    ><X size={16} /></button>
                </div>

                <div className="mt-5 space-y-3 text-sm">
                    <label className="block">
                        <div className="hf-eyebrow text-[10px]">Aktion</div>
                        <div className="relative">
                            <select
                                value={action}
                                onChange={(e) => setAction(e.target.value as UseAsAction)}
                                data-testid="medien-use-as-action"
                                className="hf-input w-full pr-8 appearance-none"
                            >
                                <option value="add_to_gallery">Zu Projekt-Galerie hinzufügen</option>
                                <option value="set_as_cover">Als Projekt-Cover setzen</option>
                                {isPhotoish && <option value="set_as_pair">Als Vorher/Nachher-Bild in Projekt</option>}
                            </select>
                            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </label>

                    <label className="block">
                        <div className="hf-eyebrow text-[10px]">Projekt</div>
                        <div className="relative">
                            <select
                                value={projectId}
                                onChange={(e) => setProjectId(e.target.value)}
                                data-testid="medien-use-as-project"
                                className="hf-input w-full pr-8 appearance-none"
                            >
                                <option value="">— bitte wählen —</option>
                                {projects.map((p) => (
                                    <option key={p.id} value={p.id}>{p.title} ({p.slug})</option>
                                ))}
                            </select>
                            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </label>

                    {action === "set_as_pair" && (
                        <>
                            <div>
                                <div className="hf-eyebrow text-[10px]">Rolle</div>
                                <div className="flex gap-2 mt-1">
                                    <button type="button"
                                        onClick={() => setPairRole("before")}
                                        data-testid="medien-use-as-pair-before"
                                        className="px-3 py-1.5 rounded-sm text-[12px]"
                                        style={{
                                            background: pairRole === "before" ? "hsl(var(--hf-gold-soft) / 0.7)" : "hsl(var(--hf-surface))",
                                            color: pairRole === "before" ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-ink))",
                                            border: "1px solid hsl(var(--hf-line))",
                                        }}
                                    >Vorher</button>
                                    <button type="button"
                                        onClick={() => setPairRole("after")}
                                        data-testid="medien-use-as-pair-after"
                                        className="px-3 py-1.5 rounded-sm text-[12px]"
                                        style={{
                                            background: pairRole === "after" ? "hsl(var(--hf-gold-soft) / 0.7)" : "hsl(var(--hf-surface))",
                                            color: pairRole === "after" ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-ink))",
                                            border: "1px solid hsl(var(--hf-line))",
                                        }}
                                    >Nachher</button>
                                </div>
                            </div>
                            <label className="block">
                                <div className="hf-eyebrow text-[10px]">Paar-Titel (optional)</div>
                                <input
                                    value={pairTitle}
                                    onChange={(e) => setPairTitle(e.target.value)}
                                    placeholder="z.B. Wohnzimmer"
                                    className="hf-input w-full"
                                    data-testid="medien-use-as-pair-title"
                                />
                            </label>
                        </>
                    )}
                </div>

                {error && (
                    <div className="mt-3 p-2 text-xs rounded"
                        style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}
                    >{error}</div>
                )}

                <div className="mt-5 flex items-center justify-end gap-2">
                    <button type="button" onClick={onClose}
                        className="hf-btn"
                        style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))", color: "hsl(var(--hf-ink))" }}
                    >Abbrechen</button>
                    <button type="button" onClick={() => void submit()}
                        disabled={saving || !projectId}
                        data-testid="medien-use-as-submit"
                        className="hf-btn"
                        style={{
                            background: "hsl(var(--hf-gold))",
                            color: "hsl(var(--hf-ink))",
                            border: "1px solid hsl(var(--hf-gold))",
                            opacity: saving || !projectId ? 0.5 : 1,
                        }}
                    >{saving ? "Speichert…" : "Speichern"}</button>
                </div>
            </div>
        </div>
    );
}
